import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'
import { TGoal, TGoalPhase, TGoalType } from '../../goalTypes'
import { TStatedCapacity, capacityFromLabel, endDateForTimeframe } from './propertyOptions'
import { TGoalStartingPoint } from './startingPoint'

export type TDraftItemKind = 'cadence' | 'oneoff' | 'milestone'

export interface TDraftPlanItem {
    id: string
    kind: TDraftItemKind
    title: string
    frequencyLabel?: string
    included: boolean
}

export interface TGoalDraft {
    title: string
    why: string
    timeframeLabel: string
    capacityLabel: string
    items: TDraftPlanItem[]
    recurringTemplateIds?: string[]
    startingPoint?: TGoalStartingPoint
    /** Set when the user picked a typed path; manual fallback hosts an explicit picker. */
    goalType?: TGoalType
    /** Ordered plan phases — rendered as the horizontal timeline when length > 1. */
    phases?: TGoalPhase[]
}

/**
 * One AI-returned path — the decompose call returns 1–2 of these, each shaped
 * within stated capacity (GOALS-V1-SCOPE §Creation flow). The user picks the
 * one that fits their life; type is AI-inferred, user-confirmed.
 */
export interface TScenarioPath {
    id: string
    type: TGoalType
    /** Short handle for the choice card, e.g. "Steady sessions". */
    label: string
    /** The path's shape in one line, e.g. "4 focused sessions / week". */
    shapeLabel: string
    /** Why the AI thinks this framing fits — one plain sentence. */
    rationale: string
    phases: TGoalPhase[]
    items: TDraftPlanItem[]
}

const WEEKLY_FREQUENCY = /^(\d+)\s*[x×]\s*\/\s*week/i

/** Sessions per week a cadence plan asks for; monthly items don't set a weekly pace. */
export const weeklyTargetForCadences = (cadences: TDraftPlanItem[]): number | undefined => {
    if (cadences.length === 0) return undefined
    const total = cadences.reduce((sum, c) => sum + Number(c.frequencyLabel?.match(WEEKLY_FREQUENCY)?.[1] ?? 0), 0)
    return total > 0 ? total : 1
}

/**
 * The weekly number the meter will hold the user to. A stated capacity is a ceiling *and* a
 * commitment, so it wins over anything the planner returned:
 *
 * - Hours stated: that is the target. Nothing else in a drafted plan measures hours, so there is
 *   nothing to reconcile it against.
 * - Sessions stated: cap the plan's own weekly ask. Taking the stated number flat would print
 *   "0 of 3" over two visible weekly rows — a week only reachable by logging by hand — so the
 *   smaller of the two is the honest target, and the ceiling still holds when the plan asks more.
 * - Nothing stated: read the planner's plan. If the user is starting from a blank consistency
 *   draft, begin with the smallest repeatable version: once a week. Concrete milestone or one-off
 *   plans stay self-graded.
 */
export const weeklyTargetForDraft = ({
    stated,
    cadenceTarget,
    modelWeeklyHours,
    isTime,
    hasUnpacedPlan,
}: {
    stated?: TStatedCapacity
    cadenceTarget?: number
    modelWeeklyHours?: number
    isTime: boolean
    hasUnpacedPlan: boolean
}): number | undefined => {
    if (stated?.unit === 'hrs') return stated.value
    if (stated) return Math.min(cadenceTarget ?? stated.value, stated.value)
    if (isTime) return modelWeeklyHours && modelWeeklyHours > 0 ? modelWeeklyHours : undefined
    return cadenceTarget ?? (hasUnpacedPlan ? undefined : 1)
}

/**
 * Included one-offs become linked tasks, not contributor labels. A completed linked task is
 * one of only three things the server credits toward a goal (the others being a recurring
 * hook and a manual log — backend/api/goal_progress.go), so a one-off written as a label
 * would be a chip that can never move the meter. Titles are the whole payload; the caller
 * owns creating and linking them once the goal has a real id.
 */
export const plannedTaskTitlesForDraft = (draft: TGoalDraft): string[] =>
    draft.items
        .filter((item) => item.included && item.kind === 'oneoff')
        .map((item) => item.title.trim())
        .filter((title) => title.length > 0)

/**
 * Weekly labels, contract line and (for paced goals) status are derived by the server from these fields.
 * Cadences become advisory chips and milestones become self-graded contributors; one-offs are
 * deliberately absent here — see plannedTaskTitlesForDraft.
 */
export const buildGoalFromDraft = (draft: TGoalDraft): TGoal => {
    const included = draft.items.filter((i) => i.included)
    const cadences = included.filter((i) => i.kind === 'cadence')
    const milestones = included.filter((i) => i.kind === 'milestone')
    const hasUnpacedPlan = included.some((i) => i.kind !== 'cadence')
    const isTime = draft.goalType === 'time'
    const stated = capacityFromLabel(draft.capacityLabel)
    const weeklyTarget = weeklyTargetForDraft({
        stated,
        cadenceTarget: weeklyTargetForCadences(cadences),
        modelWeeklyHours: draft.phases?.[0]?.weeklyHours,
        isTime,
        hasUnpacedPlan,
    })
    // The unit is the user's pick when they made one. goalType only labels the goal (GoalDetails reads
    // `unit`, never the type), so an hours capacity on a cadence-shaped plan is coherent, not a clash.
    const isHours = stated ? stated.unit === 'hrs' : isTime
    const milestoneTargetLabel = milestones.length === 1 ? '1 milestone' : `${milestones.length} milestones`
    return {
        // Goal routes use Mongo ObjectIDs. The server accepts this id on create so the optimistic
        // card and every later action address the same durable goal without an id handoff.
        id: uuidv4().replace(/-/g, '').slice(0, 24),
        title: draft.title,
        why: draft.why,
        timeframeLabel: draft.timeframeLabel,
        targetLabel: milestones.length > 0 ? milestoneTargetLabel : 'Self-graded',
        paceLabel: 'Just created — pace starts this week',
        progress: 0,
        progressLabel: milestones.length > 0 ? `0 of ${milestones.length}` : '0%',
        status: 'on_track',
        progressMode: weeklyTarget ? 'cadence' : 'manual',
        goalType: draft.goalType,
        phases: draft.phases,
        // These are the plan the user accepted, not recurring-task hooks. They survive creation and
        // redraft so GoalDetails can show the reviewed cadence without promising generated work.
        planCadences: cadences
            .map((cadence) => ({
                title: cadence.title.trim(),
                frequencyLabel: cadence.frequencyLabel ?? '',
            }))
            .filter((cadence) => cadence.title.length > 0),
        endDate: endDateForTimeframe(draft.timeframeLabel),
        weeklyTarget,
        unit: isHours ? 'hrs' : undefined,
        startDate: DateTime.now().startOf('week').toISODate(),
        recent: [],
    }
}
